import type { DesignAsset, DesignShape } from '../../types/design'
import {
  createFormField,
  validateFormDefinitionDraft,
  type FormDefinitionDraft,
  type FormField,
} from '../../types/forms'
import type { MaterializationAdapter, MaterializationContext, MaterializationResult } from './index'

const FIELD_ROLES = new Set(['FORM_FIELD'])
const SECTION_ROLES = new Set(['FORM_SECTION'])

const isFieldShape = (shape: DesignShape): boolean => {
  if (shape.semanticRole && FIELD_ROLES.has(shape.semanticRole)) return true
  if (shape.semanticRole) return false
  return shape.type === 'RECT' || shape.type === 'ROUNDED_RECT' || shape.type === 'PROCESS'
}

const isSectionShape = (shape: DesignShape): boolean =>
  shape.semanticRole ? SECTION_ROLES.has(shape.semanticRole) : shape.type === 'BUBBLE'

const trimmedLabel = (raw: string | undefined, fallback: string): string => {
  const value = (raw ?? '').trim()
  return value.length ? value.slice(0, 160) : fallback
}

const buildField = (shape: DesignShape, order: number): FormField => {
  const field = createFormField('TEXT', order)
  field.label = trimmedLabel(shape.text ?? shape.name, `Field ${order + 1}`)
  return field
}

const runFormAdapter = (
  asset: DesignAsset,
  ctx: MaterializationContext,
): MaterializationResult<FormDefinitionDraft> => {
  const warnings: string[] = []

  const fieldShapes = asset.shapes
    .filter(isFieldShape)
    .slice()
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)

  if (fieldShapes.length === 0) {
    return {
      ok: false,
      errors: [
        'This design has no shapes tagged as FORM_FIELD (or eligible rectangle/process shapes) to materialize into form fields.',
      ],
    }
  }

  const sectionShapes = asset.shapes.filter(isSectionShape)
  if (sectionShapes.length > 0) {
    warnings.push(
      `${sectionShapes.length} section shape${sectionShapes.length === 1 ? '' : 's'} detected — sections will be honored in Phase B; fields land in a single step for now.`,
    )
  }

  const fields = fieldShapes.map(buildField)

  const draft: FormDefinitionDraft = {
    id: crypto.randomUUID(),
    workspaceId: ctx.workspaceId,
    title: trimmedLabel(asset.title, 'Untitled form'),
    description: '',
    fields,
    logic: { version: 1, rules: [] },
    theme: {},
    lifecycleStatus: 'DRAFT',
    deadline: null,
    isLocked: false,
    createdBy: ctx.actor.id,
    lastSubmissionResponseId: null,
  }

  const errors = validateFormDefinitionDraft(draft)
  if (errors.length > 0) return { ok: false, errors }
  return { ok: true, draft, warnings }
}

export const formAdapter: MaterializationAdapter<FormDefinitionDraft> = {
  kind: 'FORM_LAYOUT',
  supportedRoles: ['FORM_FIELD', 'FORM_SECTION'],
  run: runFormAdapter,
}
